import { useEffect, useRef } from "react";

export type ServerEvent = {
  type: string;
  applicationId?: number;
  decision?: string;
  [key: string]: unknown;
};

/**
 * Subscribes to the backend live-update stream (`GET /api/events`, Server-Sent
 * Events) and calls `onEvent` for each pushed event. Auth rides on the HttpOnly
 * cookie, which the browser attaches to the EventSource request automatically.
 *
 * The browser's EventSource reconnects on its own after a drop (honoring the
 * server's `retry:` hint), so this hook only manages open/close on `enabled`.
 */
export function useServerEvents(
  enabled: boolean,
  onEvent: (event: ServerEvent) => void,
): void {
  const handlerRef = useRef(onEvent);
  handlerRef.current = onEvent;

  useEffect(() => {
    if (!enabled || typeof window === "undefined" || !("EventSource" in window)) {
      return undefined;
    }

    const source = new EventSource("/api/events", { withCredentials: true });

    source.onmessage = (e) => {
      if (!e?.data) return;
      let parsed: ServerEvent | null = null;
      try {
        parsed = JSON.parse(e.data);
      } catch (_) {
        return;
      }
      if (!parsed || parsed.type === "connected") return;
      try {
        handlerRef.current(parsed);
      } catch (_) {
        /* consumer errors shouldn't tear down the stream */
      }
    };

    return () => {
      try {
        source.close();
      } catch (_) {}
    };
  }, [enabled]);
}
